// Phase SA-8 — Per-slip history row.
//
// Collapsed: date · outcome badge · legs count · combined odds ·
//            bookmaker · verdict-piece spans · chev
// Expanded: autopsy_summary paragraph + <LegCardList /> (legs
//           lazy-loaded via useAutopsyLegs once expanded).

import { useState } from "react";

import {
  formatOdds,
  formatSlipDate,
  type SlipAutopsyRow,
  verdictSummaryPieces,
} from "../../slips/autopsyHistoryHelpers.ts";
import { useAutopsyLegs } from "../../slips/useAutopsyLegs.ts";
import { LegCardList } from "./LegCardList.tsx";

export interface AutopsyHistoryRowProps {
  row: SlipAutopsyRow;
  pro?: boolean;
}

export function AutopsyHistoryRow({
  row,
  pro = false,
}: AutopsyHistoryRowProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const { legs, loading } = useAutopsyLegs(row.autopsy_id, expanded);
  const pieces = verdictSummaryPieces(row);

  return (
    <div
      className="sa-history-row"
      data-testid={`sa-history-row-${row.autopsy_id}`}
      data-expanded={expanded || undefined}
    >
      <button
        type="button"
        className="sa-history-row-trigger"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={`sa-history-row-body-${row.autopsy_id}`}
        data-testid={`sa-history-row-toggle-${row.autopsy_id}`}
      >
        <span className="sa-history-row-date">
          {formatSlipDate(row.slip_date ?? row.created_at)}
        </span>
        <span
          className="sa-outcome-badge"
          data-outcome={row.outcome ?? "unknown"}
        >
          {row.outcome ?? "—"}
        </span>
        <span className="sa-history-row-legs">{`${row.leg_count}L`}</span>
        <span className="sa-history-row-odds">{formatOdds(row.combined_odds)}</span>
        <span className="sa-history-row-bookmaker">
          {row.bookmaker ?? "—"}
        </span>
        <span
          className="sa-history-row-verdict"
          data-testid={`sa-history-row-verdict-${row.autopsy_id}`}
        >
          {pieces.length === 0 ? (
            <span style={{ color: "var(--sa-fg-muted)" }}>No verdicts</span>
          ) : (
            pieces.map((p, i) => (
              <span key={p.label}>
                <span
                  className="sa-history-row-verdict-piece"
                  data-tone={p.tone}
                >
                  {p.label}
                </span>
                {i < pieces.length - 1 ? (
                  <span style={{ color: "var(--sa-fg-muted)" }}> · </span>
                ) : null}
              </span>
            ))
          )}
        </span>
        <span className="sa-history-row-chev" aria-hidden>
          ▸
        </span>
      </button>

      {expanded ? (
        <div
          id={`sa-history-row-body-${row.autopsy_id}`}
          className="sa-history-row-body"
          data-testid={`sa-history-row-body-${row.autopsy_id}`}
        >
          {row.autopsy_summary ? (
            <p
              className="sa-history-row-summary"
              data-testid={`sa-history-row-summary-${row.autopsy_id}`}
            >
              {row.autopsy_summary}
            </p>
          ) : null}
          {loading ? (
            <div className="sa-history-row-loading">Loading legs…</div>
          ) : (
            <LegCardList legs={legs} pro={pro} />
          )}
        </div>
      ) : null}
    </div>
  );
}
